/**
 * LOGOS v5.2 — Positive Geometry Panel (Full-Width Layout)
 * 
 * Layout:
 *   TOP:    3D Polytope (left) + Λ-Slice cross-section (right) — full width
 *   MIDDLE: Compact metrics row + basin/poles
 *   BOTTOM: AI interpretation — full width
 * 
 * Mathematical Foundation:
 *   [0,1]²⁸ is a positive geometry (Arkani-Hamed, Bai, Lam 2017).
 *   Canonical form Ω = ∏ 1/(xᵢ(1-xᵢ)) with poles at all 56 boundary facets.
 *   Truncated Polytope P⁺_Λ(c) adds the 57th facet {Λ = c}.
 *   Levin signals ↔ poles at xᵢ→0; Hoffman biases ↔ poles at xᵢ→1.
 * 
 * @author Dr. José Manuel Cadena Ortiz de Montellano
 * @framework Cadena Strategic Systems — Positive Geometry Layer
 * @reference arXiv:1703.04541
 */

import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { computePositiveGeometry } from '../core/positive-geometry';
import PolytopeVisualization from './PolytopeVisualization';
import useIsMobile from '../hooks/useIsMobile';

const BACKEND_URL = import.meta.env.VITE_API_URL || 'https://logoilab.com/api';

const COLORS = {
  bg: '#0F0F1A', card: '#1A1A2E', border: '#2D2D44',
  text: '#E0E0F0', textDim: '#8888AA', textMuted: '#666688',
  logos: '#C084FC', gold: '#FBBF24', green: '#10B981',
  red: '#EF4444', blue: '#6366F1', cyan: '#06B6D4',
};

const fonts = {
  mono: "'JetBrains Mono', 'Fira Code', monospace",
  sans: "'Inter', -apple-system, sans-serif",
};

function gciColor(gci) {
  if (gci >= 0.70) return COLORS.green;
  if (gci >= 0.45) return COLORS.gold;
  return COLORS.red;
}

// ═══ Λ-Slice Cross-Section SVG ═══
// Shows the truncated polytope P⁺_Λ(0.35) in the (Λ, 1−S) plane
function LambdaSlice({ lambda, entropy, volumeRatio, separatrixDistance, basin, width = 280, height = 300 }) {
  const pad = { top: 28, right: 16, bottom: 28, left: 36 };
  const w = width - pad.left - pad.right;
  const h = height - pad.top - pad.bottom;
  const sepX = pad.left + 0.35 * w; // Λ = 0.35 separatrix
  const order = 1 - entropy; // 1−S axis
  const ptX = pad.left + lambda * w;
  const ptY = pad.top + (1 - order) * h;
  const emX = pad.left + 0.65 * w; // emergence threshold

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}
      style={{ background: '#08081a', borderRadius: 8, display: 'block' }}>

      {/* P⁻ zone (red, left of separatrix) */}
      <rect x={pad.left} y={pad.top} width={sepX - pad.left} height={h}
        fill="rgba(239,68,68,0.06)" />
      {/* P⁺ zone (green, right of separatrix) */}
      <rect x={sepX} y={pad.top} width={pad.left + w - sepX} height={h}
        fill="rgba(16,185,129,0.06)" />

      {/* Emergence hypothesis zone (Λ ≥ 0.65, 1−S ≥ 0.70) */}
      <rect x={emX} y={pad.top} width={pad.left + w - emX} height={h * 0.30}
        fill="rgba(168,85,247,0.08)" stroke="rgba(168,85,247,0.15)" strokeWidth={0.5}
        strokeDasharray="3,3" />
      <text x={emX + 4} y={pad.top + 12} fontSize={7} fill="#a855f7" opacity={0.6}>
        Emergence Zone
      </text>

      {/* Grid lines */}
      {[0.2, 0.4, 0.6, 0.8].map(v => (
        <g key={`g${v}`}>
          <line x1={pad.left + v * w} y1={pad.top} x2={pad.left + v * w} y2={pad.top + h}
            stroke="rgba(255,255,255,0.04)" strokeWidth={0.5} />
          <line x1={pad.left} y1={pad.top + (1 - v) * h} x2={pad.left + w} y2={pad.top + (1 - v) * h}
            stroke="rgba(255,255,255,0.04)" strokeWidth={0.5} />
        </g>
      ))}

      {/* Separatrix line Λ = 0.35 */}
      <line x1={sepX} y1={pad.top - 4} x2={sepX} y2={pad.top + h + 4}
        stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="6,3" opacity={0.7} />
      <text x={sepX + 3} y={pad.top - 6} fontSize={7} fill="#f59e0b" opacity={0.8}>
        Λ=0.35
      </text>

      {/* Emergence threshold Λ = 0.65 */}
      <line x1={emX} y1={pad.top} x2={emX} y2={pad.top + h}
        stroke="#a855f7" strokeWidth={0.8} strokeDasharray="3,4" opacity={0.4} />

      {/* A⁺ attractor (0.85, 0.90) */}
      <circle cx={pad.left + 0.85 * w} cy={pad.top + 0.10 * h} r={6}
        fill="none" stroke="#10b981" strokeWidth={1.5} opacity={0.6} />
      <text x={pad.left + 0.85 * w} y={pad.top + 0.10 * h + 3}
        textAnchor="middle" fontSize={8} fontWeight="bold" fill="#10b981" opacity={0.8}>A⁺</text>

      {/* A⁻ attractor (0.10, 0.15) */}
      <circle cx={pad.left + 0.10 * w} cy={pad.top + 0.85 * h} r={6}
        fill="none" stroke="#ef4444" strokeWidth={1.5} opacity={0.6} />
      <text x={pad.left + 0.10 * w} y={pad.top + 0.85 * h + 3}
        textAnchor="middle" fontSize={8} fontWeight="bold" fill="#ef4444" opacity={0.8}>A⁻</text>

      {/* Current state point "TÚ" */}
      <circle cx={ptX} cy={ptY} r={8}
        fill={basin === 'A+' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}
        stroke={basin === 'A+' ? '#10b981' : '#ef4444'} strokeWidth={2} />
      <circle cx={ptX} cy={ptY} r={3}
        fill={basin === 'A+' ? '#10b981' : '#ef4444'} />
      <text x={ptX} y={ptY - 12}
        textAnchor="middle" fontSize={9} fontWeight="bold"
        fill={basin === 'A+' ? '#10b981' : '#ef4444'}>TÚ</text>

      {/* Δ distance line to separatrix */}
      <line x1={ptX} y1={ptY} x2={sepX} y2={ptY}
        stroke="rgba(255,255,255,0.15)" strokeWidth={0.8} strokeDasharray="2,2" />
      <text x={(ptX + sepX) / 2} y={ptY + 12}
        textAnchor="middle" fontSize={7} fill="#8888aa">
        Δ={separatrixDistance >= 0 ? '+' : ''}{separatrixDistance.toFixed(2)}
      </text>

      {/* Axis labels */}
      <text x={pad.left + w / 2} y={height - 4}
        textAnchor="middle" fontSize={8} fill="#8888aa">Λ (Alineación)</text>
      <text x={8} y={pad.top + h / 2}
        textAnchor="middle" fontSize={8} fill="#8888aa"
        transform={`rotate(-90, 8, ${pad.top + h / 2})`}>1−S (Orden)</text>

      {/* Axis ticks */}
      {[0, 0.2, 0.4, 0.6, 0.8, 1.0].map(v => (
        <g key={`t${v}`}>
          <text x={pad.left + v * w} y={pad.top + h + 14}
            textAnchor="middle" fontSize={6} fill="#555577">{v.toFixed(1)}</text>
          <text x={pad.left - 6} y={pad.top + (1 - v) * h + 3}
            textAnchor="end" fontSize={6} fill="#555577">{v.toFixed(1)}</text>
        </g>
      ))}

      {/* Volume ratio label */}
      <text x={pad.left + w - 4} y={pad.top + h - 6}
        textAnchor="end" fontSize={7} fill="#666688">
        Vol(P⁺)={(volumeRatio * 100).toFixed(1)}%
      </text>

      {/* P⁺ / P⁻ labels */}
      <text x={sepX + (pad.left + w - sepX) / 2} y={pad.top + h - 16}
        textAnchor="middle" fontSize={10} fontWeight="bold" fill="#10b981" opacity={0.25}>P⁺</text>
      <text x={pad.left + (sepX - pad.left) / 2} y={pad.top + h - 16}
        textAnchor="middle" fontSize={10} fontWeight="bold" fill="#ef4444" opacity={0.25}>P⁻</text>

      {/* Title */}
      <text x={width / 2} y={14}
        textAnchor="middle" fontSize={9} fontWeight="bold" fill="#C084FC">
        Λ-Slice · Politopo Truncado
      </text>
    </svg>
  );
}

export default function PositiveGeometryCard({ state, lambda, lambdaSignal, aiConsent = false, onRequestConsent }) {
  const { t } = useTranslation();
  const mobile = useIsMobile();
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(820);
  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver(entries => {
      for (const e of entries) setContainerW(e.contentRect.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const pg = useMemo(
    () => computePositiveGeometry(state, lambda, lambdaSignal),
    [state, lambda, lambdaSignal]
  );

  const { canonicalForm, boundaryProximity, truncatedPolytope, domainFactorizability, gci } = pg;
  const entropy = typeof state === 'object'
    ? 1 - (Object.values(state).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0) / Math.max(1, Object.keys(state).length))
    : 0.5;

  // ═══ AI Interpretation state (same pattern as PanelWithAI) ═══
  const [interp, setInterp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const cacheKey = 'logos_panel_positiveGeometry';

  useEffect(() => {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try {
        const p = JSON.parse(cached);
        if (Date.now() - p.ts < 600000) setInterp(p.result);
      } catch {}
    }
  }, []);

  const generate = async () => {
    if (!aiConsent) {
      if (onRequestConsent) onRequestConsent();
      return;
    }
    setLoading(true); setError(null);
    try {
      const resp = await fetch(`${BACKEND_URL}/protocol/interpret-panel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          panel: 'positiveGeometry',
          data: {
            gci, lambda,
            normalizedOmega: canonicalForm.normalizedOmega,
            boundaryProximity: boundaryProximity.globalMinDistance,
            criticalDimension: boundaryProximity.criticalDimension,
            criticalBoundaryType: boundaryProximity.criticalBoundaryType,
            basin: truncatedPolytope.basin,
            separatrixDistance: truncatedPolytope.separatrixDistance,
            volumeRatio: truncatedPolytope.volumeRatio,
            factorizability: domainFactorizability.factorizability,
            dominantDomain: domainFactorizability.dominantDomain,
            nearestPoles: canonicalForm.nearestPoles.map(p => `${p.key}(ρ=${p.proximity.toFixed(2)})`).join(', '),
          },
          language: i18n.language || 'es',
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      if (json.result) {
        setInterp(json.result);
        sessionStorage.setItem(cacheKey, JSON.stringify({ result: json.result, ts: Date.now() }));
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  // ═══ Render error boundary for Canvas ═══
  const [vizError, setVizError] = useState(false);

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)', border: `1px solid ${COLORS.logos}30`,
      borderRadius: 16, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '10px 16px', borderBottom: `1px solid ${COLORS.logos}20`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>⬡</span>
        <span style={{ fontFamily: fonts.mono, fontSize: 11, letterSpacing: '0.12em', color: COLORS.logos, textTransform: 'uppercase', fontWeight: 600 }}>
          {t('positiveGeometry.title', 'Geometría Positiva — Politopo de Consciencia')}
        </span>
      </div>

      {/* ═══ TOP: Full-width visualizations ═══ */}
      <div ref={containerRef} style={{
        display: 'grid',
        gridTemplateColumns: mobile ? '1fr' : '1fr auto',
        gap: 0, padding: mobile ? 8 : 12, paddingBottom: 0,
      }}>

        {/* LEFT: 3D Polytope */}
        <div style={{ overflow: 'hidden' }}>
          {!vizError ? (
            <ErrorCatcher onError={() => setVizError(true)}>
              <PolytopeVisualization state={state} lambda={lambda} lambdaSignal={lambdaSignal}
                width={mobile ? Math.min(containerW - 16, 480) : Math.min(containerW * 0.62, 520)}
                height={mobile ? 280 : 360} t={t} />
            </ErrorCatcher>
          ) : (
            <div style={{ textAlign: 'center', padding: 20, color: COLORS.textMuted, fontSize: 11 }}>
              {t('positiveGeometry.vizUnavailable', 'Visualización no disponible en este navegador')}
            </div>
          )}
        </div>

        {/* RIGHT (or BELOW on mobile): Λ-Slice cross-section */}
        <div style={{
          borderLeft: mobile ? 'none' : `1px solid ${COLORS.logos}10`,
          borderTop: mobile ? `1px solid ${COLORS.logos}10` : 'none',
          paddingLeft: mobile ? 0 : 8,
          paddingTop: mobile ? 8 : 0,
        }}>
          <LambdaSlice
            lambda={lambda}
            entropy={entropy}
            volumeRatio={truncatedPolytope.volumeRatio}
            separatrixDistance={truncatedPolytope.separatrixDistance}
            basin={truncatedPolytope.basin}
            width={mobile ? Math.min(containerW - 16, 360) : 200}
            height={mobile ? 180 : 260}
          />
        </div>
      </div>

      {/* ═══ MIDDLE: Compact metrics + basin/poles ═══ */}
      <div style={{ padding: '8px 12px' }}>
        {/* Metrics row */}
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? 'repeat(3, 1fr)' : 'repeat(6, 1fr)', gap: 6 }}>
          <MiniMetric label="GCI" value={Math.round(gci * 100)} color={gciColor(gci)}
            tip={t('positiveGeometry.tipGCI', 'GCI (Geometric Consciousness Index): Compuesto de Ω, ρ, factorizabilidad y distancia a separatriz. Tu "nota geométrica" de 0 a 100. >70 = favorable.')} />
          <MiniMetric label="Ω" value={`${Math.round(canonicalForm.normalizedOmega * 100)}%`} color={canonicalForm.normalizedOmega >= 0.50 ? COLORS.cyan : COLORS.gold}
            tip={t('positiveGeometry.tipOmega', 'Ω (Forma Canónica normalizada): Mide qué tan lejos estás de TODAS las fronteras del politopo simultáneamente. Alto = saludable, lejos de polos. Bajo = cerca de algún polo peligroso.')} />
          <MiniMetric label="ρ" value={boundaryProximity.globalMinDistance.toFixed(2)} color={boundaryProximity.globalMinDistance < 0.15 ? COLORS.red : COLORS.green}
            tip={t('positiveGeometry.tipRho', 'ρ (Proximidad frontera): Distancia mínima a cualquier polo de Ω. Si ρ < 0.15 = zona de peligro, alguna dimensión está cerca de colapso (0) o saturación (1). Objetivo: ρ > 0.15.')} />
          <MiniMetric label="F" value={`${Math.round(domainFactorizability.factorizability * 100)}%`} color={domainFactorizability.factorizability >= 0.70 ? COLORS.green : COLORS.gold}
            tip={t('positiveGeometry.tipFact', 'F (Factorizabilidad): Grado en que Ω se descompone en producto de dominios independientes. Alto = dominios bien separados (como una orquesta afinada). Bajo = dominios "entrelazados" (sonidos mezclados).')} />
          <MiniMetric label={truncatedPolytope.basin} value={`Δ${truncatedPolytope.separatrixDistance >= 0 ? '+' : ''}${truncatedPolytope.separatrixDistance.toFixed(2)}`}
            color={truncatedPolytope.basin === 'A+' ? COLORS.green : COLORS.red}
            tip={t('positiveGeometry.tipBasin', 'Cuenca: A+ = convergencia (círculo virtuoso hacia el Logos). A− = espiral entrópica. Δ = distancia a la separatriz (Λ=0.35).')} />
          <MiniMetric label="Vol(P⁺)" value={`${(truncatedPolytope.volumeRatio * 100).toFixed(0)}%`}
            color={truncatedPolytope.volumeRatio >= 0.50 ? COLORS.green : COLORS.gold}
            tip={t('positiveGeometry.tipFooter', 'Vol(P⁺) = volumen de la cuenca de convergencia = probabilidad teórica P(A⁺). La separatriz Λ=0.35 corta el hipercubo [0,1]²⁸ creando la 57ª faceta.')} />
        </div>

        {/* Nearest poles row */}
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
          {canonicalForm.nearestPoles.map((pole, i) => (
            <span key={i}
              title={`${pole.key}: ρ=${pole.proximity.toFixed(3)}. ${pole.proximity < 0.15 ? '⚠ PELIGRO: Esta dimensión está cerca de un polo de Ω (colapso o saturación). Requiere atención urgente.' : 'Distancia segura al polo más cercano.'} Los polos son las fronteras del politopo donde Ω "explota" — corresponden a señales Levin (x→0) o sesgos Hoffman (x→1).`}
              style={{
                fontSize: 8, fontFamily: fonts.mono, padding: '2px 6px', borderRadius: 4, cursor: 'help',
                background: pole.proximity < 0.15 ? '#2d101080' : '#1a1a2e',
                color: pole.proximity < 0.15 ? COLORS.red : COLORS.textMuted,
                border: `1px solid ${pole.proximity < 0.15 ? '#ef444430' : COLORS.border}`,
              }}>
              {pole.key} ρ={pole.proximity.toFixed(2)}
            </span>
          ))}
          <span style={{ fontSize: 7, color: COLORS.textMuted, fontFamily: fonts.mono, fontStyle: 'italic' }}>
            P⁺_Λ(0.35) · {truncatedPolytope.activeFacets} facetas
          </span>
        </div>
      </div>

      {/* ═══ BOTTOM: AI Interpretation — full width ═══ */}
      <div style={{ borderTop: `1px solid ${COLORS.logos}15`, padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontFamily: fonts.mono, fontSize: 9, letterSpacing: '0.15em', color: COLORS.logos }}>
            {t('positiveGeometry.aiInterpretation', 'INTERPRETACIÓN AI')}
          </span>
          <button onClick={generate} disabled={loading} style={{
            background: loading ? 'rgba(192,132,252,0.1)' : 'linear-gradient(135deg, #C084FC, #818CF8)',
            border: 'none', borderRadius: 6, padding: '5px 14px', color: '#fff',
            fontFamily: fonts.mono, fontSize: 9, cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.6 : 1, letterSpacing: '0.05em',
          }}>
            {loading ? '⏳...' : interp ? '↻' : '✦ Interpretar'}
          </button>
        </div>

        {error && <div style={{ fontSize: 10, color: COLORS.red, fontFamily: fonts.mono, marginBottom: 8 }}>Error: {error}</div>}

        {!interp && !loading && !error && (
          <div style={{ padding: '16px 20px', textAlign: 'center', opacity: 0.5 }}>
            <span style={{ fontSize: 28 }}>⬡</span>
            <p style={{ fontFamily: fonts.mono, fontSize: 9, color: '#8B85A0', margin: '6px 0 0', lineHeight: 1.5 }}>
              {t('positiveGeometry.clickInterpret', 'Haz clic en "Interpretar" para que la AI explique tu geometría de consciencia')}
            </p>
          </div>
        )}

        {loading && (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <div style={{ width: 30, height: 30, margin: '0 auto', borderRadius: '50%', border: '2px solid rgba(192,132,252,0.2)', borderTopColor: COLORS.logos, animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {interp && !loading && (
          <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            {interp.explanation && (
              <div style={{ background: 'rgba(192,132,252,0.06)', borderRadius: 8, padding: 12, borderLeft: '2px solid #C084FC' }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 8, color: COLORS.logos, marginBottom: 4, letterSpacing: '0.1em' }}>{t('positiveGeometry.whatItShows', 'QUÉ MUESTRA')}</div>
                <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.6 }}>{interp.explanation}</p>
              </div>
            )}
            {interp.insight && (
              <div style={{ background: 'rgba(245,197,66,0.06)', borderRadius: 8, padding: 12, borderLeft: '2px solid #F5C542' }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 8, color: '#F5C542', marginBottom: 4, letterSpacing: '0.1em' }}>💡 INSIGHT</div>
                <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.6, fontWeight: 500 }}>{interp.insight}</p>
              </div>
            )}
            {interp.howToUse && (
              <div style={{ background: 'rgba(6,182,212,0.06)', borderRadius: 8, padding: 12, borderLeft: '2px solid #06B6D4' }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 8, color: '#06B6D4', marginBottom: 4, letterSpacing: '0.1em' }}>{t('positiveGeometry.howToUse', 'CÓMO USAR')}</div>
                <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.6 }}>{interp.howToUse}</p>
              </div>
            )}
            {interp.action && (
              <div style={{ background: 'rgba(16,185,129,0.06)', borderRadius: 8, padding: 12, borderLeft: '2px solid #10B981' }}>
                <div style={{ fontFamily: fonts.mono, fontSize: 8, color: '#10B981', marginBottom: 4, letterSpacing: '0.1em' }}>⚡ ACCIÓN</div>
                <p style={{ fontFamily: fonts.sans, fontSize: 11, color: '#E8E4F0', margin: 0, lineHeight: 1.6 }}>{interp.action}</p>
              </div>
            )}
          </div>
        )}
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

function MiniMetric({ label, value, color, tip }) {
  const [show, setShow] = React.useState(false);
  return (
    <div
      style={{ textAlign: 'center', padding: '4px 2px', background: '#0a0a18', borderRadius: 6, cursor: tip ? 'pointer' : 'default', position: 'relative' }}
      onMouseEnter={() => tip && setShow(true)}
      onMouseLeave={() => setShow(false)}
      onClick={() => tip && setShow(s => !s)}
    >
      <div style={{ fontSize: 8, color: '#555577', fontFamily: fonts.mono }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color, fontFamily: fonts.mono }}>{value}</div>
      {show && tip && (
        <div style={{
          position: 'absolute', bottom: '100%', left: '50%', transform: 'translateX(-50%)',
          width: 220, padding: '8px 10px', marginBottom: 4,
          background: 'rgba(10,10,30,0.96)', border: `1px solid ${color}40`,
          borderRadius: 8, color: '#E0E0F0', fontSize: 10, lineHeight: 1.5,
          fontFamily: "'Inter', -apple-system, sans-serif",
          zIndex: 50, pointerEvents: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,0.6)',
          textAlign: 'left',
        }}>
          {tip}
        </div>
      )}
    </div>
  );
}

class ErrorCatcher extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch() { if (this.props.onError) this.props.onError(); }
  render() { return this.state.hasError ? null : this.props.children; }
}
